import React from 'react';
import { Box, Button, Checkbox, Dialog, DialogContent, DialogFooter, DialogTrigger, Flex, LoadingOrbit, Select, Text, TextField, Tooltip, TooltipTrigger, useToast, type SelectOption } from '@chatsdet/ui';
import type { AttachmentKind } from '@chatsdet/types';
import { attachmentRawUrl, useAttachmentList, useUploadAttachments } from '../api';
import { formatBytes, getFileTypeBadge, isImage } from '../utils';
import { AttachmentUploadDialog } from './AttachmentUploadDialog';
import { Plus } from '@phosphor-icons/react';

const kindOptions: SelectOption[] = [
  { id: 'all', label: 'All types' },
  { id: 'image', label: 'Images' },
  { id: 'pdf', label: 'PDFs' },
  { id: 'spreadsheet', label: 'Spreadsheets' },
  { id: 'document', label: 'Documents' },
  { id: 'audio', label: 'Audio' },
];

type AttachmentPickerDialogProps = {
  selectedIds: string[];
  onChange: (nextIds: string[]) => void;
  triggerLabel?: string;
  triggerVariant?: 'secondary' | 'outline' | 'ghost';
  triggerSize?: 'sm' | 'md' | 'lg';
  allowUpload?: boolean;
  /** Initial kind filter; defaults to 'all'. Useful for node types that only
   *  accept certain content types (e.g. audio nodes => 'audio'). */
  initialKind?: AttachmentKind;
};

export function AttachmentPickerDialog({
  selectedIds,
  onChange,
  triggerLabel = 'Add attachments',
  triggerVariant = 'secondary',
  triggerSize,
  allowUpload = true,
  initialKind = 'all',
}: AttachmentPickerDialogProps) {
  const { toast } = useToast();
  const [isOpen, setIsOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [kind, setKind] = React.useState<AttachmentKind>(initialKind);
  const [page, setPage] = React.useState(1);
  const initialSelectedIdsRef = React.useRef<string[] | null>(null);

  const listQuery = useAttachmentList({ page, pageSize: 24, search, kind });
  const upload = useUploadAttachments();

  React.useEffect(() => {
    if (!isOpen) {
      setSearch('');
      setKind(initialKind);
      setPage(1);
    }
  }, [isOpen]);

  const items = listQuery.data?.data ?? [];
  const pagination = listQuery.data?.pagination ?? null;

  const handleUpload = React.useCallback(
    async (files: File[]) => {
      const res = await upload.mutateAsync(files);
      const nextIds = new Set(selectedIds);
      for (const item of res.data) {
        nextIds.add(item.id);
      }
      onChange(Array.from(nextIds));
    },
    [onChange, selectedIds, upload]
  );

  return (
    <DialogTrigger
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (open) {
          initialSelectedIdsRef.current = selectedIds.slice();
        } else {
          const initial = initialSelectedIdsRef.current ?? [];
          const initialSet = new Set(initial);
          const currentSet = new Set(selectedIds);
          let added = 0;
          let removed = 0;
          for (const id of selectedIds) {
            if (!initialSet.has(id)) added += 1;
          }
          for (const id of initial) {
            if (!currentSet.has(id)) removed += 1;
          }
          if (added > 0) {
            toast.success(`Added ${added} attachment${added === 1 ? '' : 's'}`);
          } else if (removed > 0) {
            toast.info(`Removed ${removed} attachment${removed === 1 ? '' : 's'}`);
          }
          initialSelectedIdsRef.current = null;
        }

        setIsOpen(open);
        if (!open) {
          setSearch('');
          setKind('all');
          setPage(1);
        }
      }}
    >
      <Button variant={triggerVariant} size={triggerSize} iconLeft={<Plus size={14} />}>
        {triggerLabel}
      </Button>
      <Dialog title="Select attachments">
        <DialogContent className="space-y-4">
          <Flex align="end" gap="sm" className="mb-4">
            <Box className="flex-1">
              <TextField
                label="Search"
                placeholder="Search filenames…"
                value={search}
                onChange={(value) => {
                  setSearch(value);
                  setPage(1);
                }}
              />
            </Box>
            <Select
              label=""
              aria-label="Attachment type"
              items={kindOptions}
              selectedKey={kind}
              onSelectionChange={(key) => {
                setKind(String(key) as AttachmentKind);
                setPage(1);
              }}
            />
            {allowUpload && (
              <AttachmentUploadDialog
                isUploading={upload.isPending}
                onUpload={handleUpload}
                triggerLabel="Upload"
              />
            )}
          </Flex>

          {listQuery.isLoading && items.length === 0 ? (
            <Flex justify="center" className="py-10">
              <LoadingOrbit />
            </Flex>
          ) : items.length === 0 ? (
            <Text className="text-muted-foreground">No attachments found.</Text>
          ) : (
            <div className="divide-y divide-border">
              {items.map((item) => {
                  const selected = selectedIds.includes(item.id);
                  const image = isImage(item.contentType);
                  const previewUrl = image ? attachmentRawUrl(item.id) : null;

                  const setSelected = (nextSelected: boolean) => {
                    const set = new Set(selectedIds);
                    if (nextSelected) {
                      set.add(item.id);
                    } else {
                      set.delete(item.id);
                    }
                    onChange(Array.from(set));
                  };

                  const row = (
                    <Checkbox
                      key={item.id}
                      isSelected={selected}
                      onChange={setSelected}
                      className="w-full px-3 py-2 hover:bg-muted/40 focus:outline-none focus-visible:ring-1 focus-visible:ring-ring items-center"
                      label={
                        <Text as="span" className="truncate block" title={item.filename}>
                          {item.filename}
                        </Text>
                      }
                      description={
                        <Text as="span" size="xs" className="text-muted-foreground">
                          {getFileTypeBadge(item.contentType)} • {formatBytes(item.sizeBytes)}
                        </Text>
                      }
                    />
                  );

                  if (!previewUrl) {
                    return row;
                  }

                  return (
                    <TooltipTrigger key={item.id} placement="right" delay={150}>
                      {row}
                      <Tooltip className="p-2 bg-popover">
                        <div className="w-[240px]">
                          <img
                            src={previewUrl}
                            alt={item.filename}
                            className="w-full h-auto max-h-[240px] object-contain rounded-md border border-border"
                            loading="lazy"
                          />
                        </div>
                      </Tooltip>
                    </TooltipTrigger>
                  );
              })}
            </div>
          )}

          {pagination && pagination.totalPages > 1 && (
            <Flex align="center" justify="between" className="mt-4">
              <Button
                variant="secondary"
                isDisabled={page <= 1 || listQuery.isFetching}
                onPress={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </Button>
              <Text size="sm" className="text-muted-foreground">
                Page {pagination.page} of {pagination.totalPages}
              </Text>
              <Button
                variant="secondary"
                isDisabled={page >= pagination.totalPages || listQuery.isFetching}
                onPress={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
              >
                Next
              </Button>
            </Flex>
          )}
        </DialogContent>
        <DialogFooter>
          <Button variant="primary" onPress={() => setIsOpen(false)}>
            Done
          </Button>
        </DialogFooter>
      </Dialog>
    </DialogTrigger>
  );
}
